import { forwardRef, type HTMLAttributes, useImperativeHandle } from 'react'
import { twMerge } from 'tailwind-merge'
import { CardFrame } from '@/components/base/CardFrame'
import { CountdownTimer } from '@/components/base/CountdownTimer'
import { BackgroundImage } from '@/components/base/BackgroundImage'
import { RetentionHeader } from '@/components/composite/RetentionHeader'
import { CheckInItem } from '@/components/layout/CheckInItem'
import { ScrollableList } from '@/components/layout/ScrollableList'
import { GiftIcon, CalendarClockIcon } from '@/icons'
import { dailyCheckInPanelVariants } from './DailyCheckInPanel.variants'
import type { CheckInItemData } from './types'
import { useCenterHighlightedStep } from './hooks/useCenterOnHighlightedStep'

export interface DailyCheckInPanelProps extends HTMLAttributes<HTMLDivElement> {
  items: CheckInItemData[]
  bgImageUrl?: string
  headerTitle: string
  headerInfo?: string
  headerTag?: string
  countdown?: Date
  countdownPrefix?: string
  highlightedStep?: number
}

export const DailyCheckInPanel = forwardRef<HTMLDivElement, DailyCheckInPanelProps>(
  (
    {
      items,
      bgImageUrl,
      headerTitle,
      headerInfo,
      headerTag,
      countdown,
      countdownPrefix,
      highlightedStep,
      className,
      ...props
    },
    ref
  ) => {
    const accentColor = 'yellow'

    const { panelRef, listRef, getItemRef } = useCenterHighlightedStep({
      highlightedStep,
      deps: [items],
    })
    
    // Merge the forwarded ref with our internal ref
    useImperativeHandle(ref, () => panelRef.current as HTMLDivElement)

    return (
      <div
        className={twMerge(dailyCheckInPanelVariants({ accentColor }), className)}
        ref={panelRef}
        {...props}
      >
        {bgImageUrl ? (
          <BackgroundImage
            className="
              opacity-30 h-1/2 rounded-xl
              [-webkit-mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
              [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
            "
            imageSrc={bgImageUrl}
          />
        ) : null}
        <div className="absolute inset-0 bg-fade-to-transparent z-10 rounded-xl" />

        <div className="relative z-20 p-2">
          <RetentionHeader
            accentColor={accentColor}
            icon={<GiftIcon className="w-[22px] h-[22px]" />}
            subtitle={
              <>
                {countdown ? (
                  <div className="flex items-center gap-1">
                    <CalendarClockIcon className="w-4 h-4" />
                    <span>
                      {countdownPrefix ? countdownPrefix : null}{' '}
                      <CountdownTimer
                        className="text-dark-yellow font-semibold"
                        endTime={countdown}
                      />
                    </span>
                  </div>
                ) : null}
              </>
            }
            tag={headerTag}
            title={headerTitle}
            tooltip={headerInfo}
          />

          <CardFrame className="mt-4">
            <div className="rounded-xl overflow-hidden">
              <ScrollableList ref={listRef}>
                {items.map((item) => {
                  return (
                    <div key={item.step} ref={getItemRef(item.step)}>
                      <CheckInItem {...item} />
                    </div>
                  )
                })}
              </ScrollableList>
            </div>
          </CardFrame>
        </div>
      </div>
    )
  }
)

DailyCheckInPanel.displayName = 'DailyCheckInPanel'
